'use client';

import { Carousel } from '@/components';
import { asLink, asText, Content } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { useIsMobile } from '@/hooks';
import Image from 'next/image';
import { PrismicNextLink } from '@prismicio/next';

/**
 * Props for `PromotionalPhrasesCarousel`.
 */
export type PromotionalPhrasesCarouselProps =
  SliceComponentProps<Content.PromotionalPhrasesCarouselSlice>;

/**
 * Component for "PromotionalPhrasesCarousel" Slices.
 */
const PromotionalPhrasesCarousel = ({
  slice,
}: PromotionalPhrasesCarouselProps): JSX.Element => {
  const isMobile = useIsMobile();

  const {
    primary: {
      promotional_phrases: promotional_phrases,
      hidden_title: hiddenTitle,
    },
  } = slice;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="text-white -mb-16 lg:-mb-40 min-h-[600px]"
    >
      {asText(hiddenTitle) && (
        <span className="hidden">
          <PrismicRichText field={slice.primary.hidden_title} />
        </span>
      )}
      <Carousel dotType="large-mb-extra" arrows={false} autoPlaySpeed={2500}>
        {promotional_phrases.map((promotionalPhrase, index) => {
          const videoUrl = asLink(promotionalPhrase.background_media);
          const videoPoster = promotionalPhrase.background_poster;
          const buttonLink = promotionalPhrase.button_link;
          const buttonLabel = promotionalPhrase.button_label;
          return (
            <div key={index} className="relative h-full lg:pt-32 min-h-[600px]">
              <div className="absolute inset-0 z-0">
                {isMobile && (
                  <Image
                    src={videoPoster.url!}
                    alt="Video Poster"
                    layout="fill"
                    objectFit="cover"
                    className="pointer-events-none transition-opacity duration-500"
                    priority
                  />
                )}
                {!isMobile && videoUrl && (
                  <video
                    src={videoUrl}
                    autoPlay
                    loop
                    muted
                    poster={videoPoster?.url || undefined}
                    className={`w-full h-full object-cover pointer-events-none transition-opacity duration-500`}
                  >
                    <track
                      src="/silent.vtt"
                      kind="captions"
                      srcLang="en"
                      label="English"
                      default={false}
                    />
                  </video>
                )}
                <div className="absolute inset-0 bg-black opacity-70"></div>
              </div>
              <section className="relative z-10 px-16 min-h-[500px] lg:min-h-[600px]">
                <div className="pt-6 text-center lg:text-left">
                  <div className="lg:max-w-[750px] mb-4">
                    <PrismicRichText field={promotionalPhrase.title} />
                  </div>
                  <div className="lg:max-w-[650px]">
                    <PrismicRichText field={promotionalPhrase.subtitle} />
                  </div>
                </div>
                <div className="flex justify-center lg:justify-start mb-14 mt-4 lg:mt-12 sm:mt-16">
                  <PrismicNextLink
                    field={buttonLink}
                    className="button-bordered"
                    tabIndex={-1}
                  >
                    <span>{buttonLabel}</span>
                  </PrismicNextLink>
                </div>
              </section>
            </div>
          );
        })}
      </Carousel>
    </section>
  );
};

export default PromotionalPhrasesCarousel;
